import React, { useState, useRef } from "react";
import NovoEventoService from "../../services/Eventos/NovoEventoService";
import { Button, Input, Modal, Toast } from "../ui";
import { Plus, Check, Layers, Calendar, DollarSign, Users, Loader2 } from "lucide-react";

function AdicionarLote({ 
    corridaId, 
    corridaNome, 
    onLoteCriado, 
    buttonLabel = "Novo Lote", 
    buttonIcon,
    buttonClassName = "", 
    buttonVariant = "primary", 
    buttonSize = "sm" 
}) {
    const [visible, setVisible] = useState(false);
    const [nomeLote, setNomeLote] = useState("");
    const [valorLote, setValorLote] = useState("");
    const [dataInicioLote, setDataInicioLote] = useState("");
    const [dataFimLote, setDataFimLote] = useState("");
    const [qtdVagasLote, setQtdVagasLote] = useState("");
    const [loading, setLoading] = useState(false);

    const toast = useRef(null);
    const eventoService = new NovoEventoService();

    const handleOpen = () => {
        setNomeLote("");
        setValorLote("");
        setDataInicioLote("");
        setDataFimLote("");
        setQtdVagasLote("");
        setVisible(true);
    };

    const handleClose = () => {
        if (loading) return;
        setVisible(false);
        setNomeLote("");
        setValorLote("");
        setDataInicioLote("");
        setDataFimLote("");
        setQtdVagasLote("");
    };

    const handleSalvar = async () => {
        if (!corridaId) {
            toast.current?.show({
                severity: "error",
                summary: "Erro",
                detail: "Evento não identificado.",
                life: 3000
            });
            return;
        }

        if (!nomeLote || !nomeLote.trim()) {
            toast.current?.show({
                severity: "warn",
                summary: "Atenção",
                detail: "Informe o nome do lote.",
                life: 3000
            });
            return;
        }

        const numValor = parseFloat(String(valorLote).replace(',', '.'));
        if (isNaN(numValor) || numValor < 0) {
            toast.current?.show({
                severity: "warn",
                summary: "Atenção",
                detail: "Informe um valor válido para o lote.",
                life: 3000
            });
            return;
        }

        if (!dataInicioLote || !dataFimLote) {
            toast.current?.show({
                severity: "warn",
                summary: "Atenção",
                detail: "Preencha as datas de início e fim do lote.",
                life: 3000
            });
            return;
        }

        if (dataInicioLote > dataFimLote) {
            toast.current?.show({
                severity: "warn",
                summary: "Atenção",
                detail: "A data de início não pode ser posterior à data de fim.",
                life: 3000
            });
            return;
        }

        setLoading(true);

        try {
            let proximaOrdem = 1;
            try {
                const resExistentes = await eventoService.getLotesPorCorrida(corridaId);
                if (resExistentes.ok) {
                    const ltsExistentes = await resExistentes.json();
                    if (Array.isArray(ltsExistentes)) {
                        proximaOrdem = ltsExistentes.length + 1;
                    }
                }
            } catch (_) {}

            const lotePayload = {
                id_corrida: parseInt(corridaId),
                nome_lote: nomeLote.trim(),
                valor: numValor.toFixed(2),
                data_inicio: dataInicioLote,
                data_fim: dataFimLote,
                qtd_vagas: parseInt(qtdVagasLote) || 0,
                ordem: proximaOrdem
            };

            const response = await eventoService.setNovoLote(lotePayload);

            if (response.ok || response.status === 201 || response.status === 200) {
                toast.current?.show({
                    severity: "success",
                    summary: "Sucesso!",
                    detail: `Lote "${nomeLote.trim()}" adicionado com sucesso!`,
                    life: 3000
                });

                handleClose();

                if (onLoteCriado) {
                    onLoteCriado();
                }
            } else {
                let msg = "Erro ao adicionar lote.";
                try {
                    const err = await response.json();
                    if (err.detail) msg = err.detail;
                    else if (err.erro) msg = err.erro;
                    else if (err.mensagem) msg = err.mensagem;
                } catch (_) {}

                toast.current?.show({
                    severity: "error",
                    summary: "Erro",
                    detail: msg,
                    life: 4000
                });
            }
        } catch (error) {
            console.error("Erro ao cadastrar lote:", error);
            toast.current?.show({
                severity: "error",
                summary: "Erro",
                detail: "Erro de conexão ao adicionar lote.",
                life: 4000
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Toast ref={toast} />

            <Button
                type="button"
                variant={buttonVariant}
                size={buttonSize}
                className={`flex items-center gap-1.5 font-semibold ${buttonClassName}`}
                onClick={handleOpen}
            >
                {buttonIcon || <Plus className="w-4 h-4" />}
                <span>{buttonLabel}</span>
            </Button>

            <Modal
                isOpen={visible}
                onClose={handleClose}
                title="Adicionar Lote de Inscrição"
                maxWidth="max-w-xl"
            >
                <div className="flex flex-col gap-4 text-white">
                    {corridaNome && (
                        <div className="p-3 rounded-xl bg-slate-900 border-l-4 border-urb-primary border border-urb-border">
                            <div className="font-bold text-base text-urb-primary">
                                {corridaNome}
                            </div>
                            <div className="text-xs text-slate-400 mt-0.5">
                                Configure o valor, período e quantidade de vagas para o novo lote deste evento.
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                            <label htmlFor="modal_lote_nome" className="text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                                <Layers className="w-3.5 h-3.5 text-urb-primary" />
                                Nome do Lote <span className="text-red-400">*</span>
                            </label>
                            <Input
                                id="modal_lote_nome"
                                value={nomeLote}
                                onChange={(e) => setNomeLote(e.target.value)}
                                placeholder="Ex: 1º Lote, Lote Promocional, 2º Lote..."
                                disabled={loading}
                            />
                        </div>

                        <div>
                            <label htmlFor="modal_lote_valor" className="text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                                <DollarSign className="w-3.5 h-3.5 text-urb-primary" />
                                Valor (R$) <span className="text-red-400">*</span>
                            </label>
                            <Input
                                id="modal_lote_valor"
                                type="number"
                                step="0.01"
                                min="0"
                                value={valorLote}
                                onChange={(e) => setValorLote(e.target.value)}
                                placeholder="0.00"
                                disabled={loading}
                            />
                        </div>

                        <div>
                            <label htmlFor="modal_lote_vagas" className="text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                                <Users className="w-3.5 h-3.5 text-urb-primary" />
                                Quantidade de Vagas
                            </label>
                            <Input
                                id="modal_lote_vagas"
                                type="number"
                                min="0"
                                value={qtdVagasLote}
                                onChange={(e) => setQtdVagasLote(e.target.value)}
                                placeholder="0 = Ilimitado"
                                disabled={loading}
                            />
                        </div>

                        <div>
                            <label htmlFor="modal_lote_inicio" className="text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5 text-urb-primary" />
                                Início do Lote <span className="text-red-400">*</span>
                            </label>
                            <Input
                                id="modal_lote_inicio"
                                type="date"
                                value={dataInicioLote}
                                onChange={(e) => setDataInicioLote(e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        <div>
                            <label htmlFor="modal_lote_fim" className="text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5 text-urb-primary" />
                                Fim do Lote <span className="text-red-400">*</span>
                            </label>
                            <Input
                                id="modal_lote_fim"
                                type="date"
                                value={dataFimLote}
                                onChange={(e) => setDataFimLote(e.target.value)}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div className="flex justify-end gap-2.5 mt-4 pt-3 border-t border-urb-border">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleClose}
                            disabled={loading}
                            className="px-4 py-2 text-xs"
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSalvar}
                            disabled={loading}
                            className="px-5 py-2 text-xs font-bold shadow-neon flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <Check className="w-4 h-4" />
                                    Criar Lote
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            </Modal>
        </>
    );
}

export default AdicionarLote;
